import { SaveBar } from '@shopify/app-bridge-react';
import { Spinner, Text } from '@shopify/polaris';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { t } from '../i18n';

/**
 * True when running inside Shopify admin (iframe / host param).
 * Outside embed, App Bridge save bar won't appear — callers should keep an in-page Save fallback.
 */
export function isShopifyEmbedded(): boolean {
  try {
    if (typeof window === 'undefined') return false;
    const p = new URLSearchParams(window.location.search);
    if (p.get('host') || p.get('embedded') === '1') return true;
    if (sessionStorage.getItem('gf_embedded') === '1') return true;
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/** Shopify App Bridge toast (visible anywhere on the page, including bottom scroll). */
export function showAppToast(
  message: string,
  opts?: { isError?: boolean; duration?: number },
) {
  try {
    const shopify = (
      window as unknown as {
        shopify?: {
          toast?: {
            show: (
              msg: string,
              o?: { isError?: boolean; duration?: number },
            ) => void;
          };
        };
      }
    ).shopify;
    if (shopify?.toast?.show) {
      shopify.toast.show(message, {
        isError: opts?.isError === true,
        duration: opts?.duration ?? 3200,
      });
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * Single permanent SaveBar id for the whole app.
 * App Bridge hosts the chrome bar outside the iframe — if the React <SaveBar>
 * unmounts while open, hide() becomes a no-op and Save/Discard go dead.
 * Keeping one host mounted for the app lifetime avoids that.
 */
export const GLOBAL_SAVE_BAR_ID = 'freebi-app-save';

/** Legacy per-page ids (from older builds) — force-hide on boot/route change. */
export const LEGACY_SAVE_BAR_IDS = [
  'freebi-settings-save',
  'freebi-popup-save',
  'freebi-milestone-save',
  'freebi-congrats-save',
  'freebi-generic-campaign-save',
  'freebi-bxgy-save',
  'freebi-spend-gift-save',
] as const;

export const FREEBI_SAVE_BAR_IDS = [
  GLOBAL_SAVE_BAR_ID,
  ...LEGACY_SAVE_BAR_IDS,
] as const;

type ShopifySaveBarApi = {
  hide?: (id: string) => Promise<void> | void;
  show?: (id: string) => Promise<void> | void;
  toggle?: (id: string) => Promise<void> | void;
};

function getShopifySaveBar(): ShopifySaveBarApi | undefined {
  try {
    return (
      window as unknown as { shopify?: { saveBar?: ShopifySaveBarApi } }
    ).shopify?.saveBar;
  } catch {
    return undefined;
  }
}

type UiSaveBarEl = HTMLElement & {
  hide?: () => Promise<void> | void;
  show?: () => Promise<void> | void;
};

/**
 * Hide a save bar by id via host API + element API.
 * If no element exists (orphaned host chrome from a previous page), create a
 * temporary ui-save-bar so App Bridge can bind and dismiss the host bar.
 */
export function hideSaveBar(id: string) {
  try {
    void getShopifySaveBar()?.hide?.(id);
  } catch {
    /* ignore */
  }

  try {
    let el = document.getElementById(id) as UiSaveBarEl | null;
    let created = false;
    if (!el) {
      el = document.createElement('ui-save-bar') as UiSaveBarEl;
      el.id = id;
      el.style.display = 'none';
      document.body.appendChild(el);
      created = true;
    }
    try {
      void el.hide?.();
    } catch {
      /* ignore */
    }
    void getShopifySaveBar()?.hide?.(id);
    if (created) {
      // Give App Bridge a tick to process hide, then remove temp node
      window.setTimeout(() => {
        try {
          el?.remove();
        } catch {
          /* ignore */
        }
      }, 100);
    }
  } catch {
    /* ignore */
  }
}

/** Hide every known Freebi save bar (global + legacy). */
export function hideAllSaveBars() {
  for (const id of FREEBI_SAVE_BAR_IDS) {
    hideSaveBar(id);
  }
  try {
    document.querySelectorAll('ui-save-bar').forEach((node) => {
      const el = node as UiSaveBarEl;
      try {
        void el.hide?.();
      } catch {
        /* ignore */
      }
    });
  } catch {
    /* ignore */
  }
}

// ── Permanent host (never unmounts) ─────────────────────────────────────

type SaveBarRegistration = {
  open: boolean;
  saving: boolean;
  onSave: () => void | Promise<void>;
  onDiscard: () => void;
};

type SaveBarControl = {
  setRegistration: (reg: SaveBarRegistration | null) => void;
};

const SaveBarControlContext = createContext<SaveBarControl | null>(null);

/**
 * Mount once near the app root. Owns the only App Bridge <SaveBar> so buttons
 * and hide() always have a live element, even after leaving a dirty form page.
 */
export function AppSaveBarHost({ children }: { children: ReactNode }) {
  const [reg, setReg] = useState<SaveBarRegistration | null>(null);
  const regRef = useRef<SaveBarRegistration | null>(null);
  regRef.current = reg;
  const discardLock = useRef(false);

  const setRegistration = useCallback((next: SaveBarRegistration | null) => {
    regRef.current = next;
    setReg(next);
    if (!next || (!next.open && !next.saving)) {
      hideSaveBar(GLOBAL_SAVE_BAR_ID);
    }
  }, []);

  const control = useMemo(() => ({ setRegistration }), [setRegistration]);

  // Kill any orphaned host chrome bars from previous pages / old builds
  useEffect(() => {
    hideAllSaveBars();
    const timers = [0, 50, 200, 500].map((ms) =>
      window.setTimeout(() => hideAllSaveBars(), ms),
    );
    return () => {
      timers.forEach((t) => window.clearTimeout(t));
      hideAllSaveBars();
    };
  }, []);

  const handleSave = useCallback(() => {
    const r = regRef.current;
    if (!r || r.saving) return;
    void Promise.resolve(r.onSave()).catch(() => undefined);
  }, []);

  const handleDiscard = useCallback(() => {
    const r = regRef.current;
    if (!r || r.saving || discardLock.current) return;
    discardLock.current = true;
    try {
      // Optimistic close: flip open=false immediately so App Bridge <SaveBar>
      // does not re-show while the page resets field state.
      const closed: SaveBarRegistration = {
        ...r,
        open: false,
      };
      regRef.current = closed;
      setReg(closed);
      r.onDiscard();
      hideSaveBar(GLOBAL_SAVE_BAR_ID);
      window.setTimeout(() => hideSaveBar(GLOBAL_SAVE_BAR_ID), 0);
      window.setTimeout(() => hideSaveBar(GLOBAL_SAVE_BAR_ID), 80);
    } finally {
      window.setTimeout(() => {
        discardLock.current = false;
      }, 400);
    }
  }, []);

  // Native listeners — App Bridge host chrome proxies clicks to iframe buttons;
  // React synthetic events are occasionally missed after remounts.
  const saveBtnRef = useCallback(
    (node: HTMLButtonElement | null) => {
      if (!node) return;
      const fn = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        handleSave();
      };
      node.addEventListener('click', fn);
      // React will replace the node on re-render; cleanup via replace
      (node as HTMLButtonElement & { __gfSave?: () => void }).__gfSave = () =>
        node.removeEventListener('click', fn);
    },
    [handleSave],
  );

  const discardBtnRef = useCallback(
    (node: HTMLButtonElement | null) => {
      if (!node) return;
      const fn = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        handleDiscard();
      };
      node.addEventListener('click', fn);
      (node as HTMLButtonElement & { __gfDiscard?: () => void }).__gfDiscard =
        () => node.removeEventListener('click', fn);
    },
    [handleDiscard],
  );

  const open = Boolean(reg && (reg.open || reg.saving));
  const saving = Boolean(reg?.saving);

  // Keep host API in sync with React state (belt + suspenders)
  useEffect(() => {
    if (open) {
      try {
        void getShopifySaveBar()?.show?.(GLOBAL_SAVE_BAR_ID);
      } catch {
        /* ignore */
      }
    } else {
      hideSaveBar(GLOBAL_SAVE_BAR_ID);
    }
  }, [open]);

  return (
    <SaveBarControlContext.Provider value={control}>
      {children}
      <SaveBar id={GLOBAL_SAVE_BAR_ID} open={open}>
        <button
          ref={saveBtnRef}
          type="button"
          variant="primary"
          {...(saving ? { loading: '' } : {})}
          disabled={saving || undefined}
          onClick={handleSave}
        >
          {saving ? t('Saving…') : t('Save')}
        </button>
        <button
          ref={discardBtnRef}
          type="button"
          disabled={saving || undefined}
          onClick={handleDiscard}
        >
          {t("Discard")}
        </button>
      </SaveBar>
    </SaveBarControlContext.Provider>
  );
}

export interface ContextualSaveBarProps {
  /** @deprecated Ignored — single global bar id is used. Kept for call-site compatibility. */
  id?: string;
  /** Show the bar when the form has unsaved changes */
  open: boolean;
  saving?: boolean;
  onSave: () => void | Promise<void>;
  onDiscard: () => void;
  saveLabel?: string;
  discardLabel?: string;
}

/**
 * Registers this page's dirty form with the permanent AppSaveBarHost.
 * Renders nothing — the host owns the actual App Bridge element.
 */
export function ContextualSaveBar({
  open,
  saving = false,
  onSave,
  onDiscard,
}: ContextualSaveBarProps) {
  const control = useContext(SaveBarControlContext);
  const onSaveRef = useRef(onSave);
  const onDiscardRef = useRef(onDiscard);
  onSaveRef.current = onSave;
  onDiscardRef.current = onDiscard;

  useEffect(() => {
    if (!control) {
      console.warn(
        '[Freebi] ContextualSaveBar used without AppSaveBarHost — save bar will not work.',
      );
      return;
    }

    control.setRegistration({
      open,
      saving,
      onSave: () => onSaveRef.current(),
      onDiscard: () => onDiscardRef.current(),
    });

    return () => {
      control.setRegistration(null);
      hideSaveBar(GLOBAL_SAVE_BAR_ID);
      // Also clear any legacy id that might still be stuck in host chrome
      for (const legacy of LEGACY_SAVE_BAR_IDS) {
        hideSaveBar(legacy);
      }
    };
  }, [control, open, saving]);

  // No local <SaveBar> — host owns it permanently
  return null;
}

/**
 * Full-viewport save progress that works even when scrolled to the bottom.
 * Soft Polaris-style card + spinner (blends with admin app chrome).
 */
export function SavingOverlay({
  open,
  label = 'Saving…',
  detail,
}: {
  open: boolean;
  label?: string;
  detail?: string;
}) {
  if (!open) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 520,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(241, 241, 241, 0.55)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
        pointerEvents: 'all',
      }}
    >
      <div
        style={{
          minWidth: 220,
          maxWidth: 320,
          padding: '22px 28px',
          borderRadius: 14,
          background: '#fff',
          boxShadow:
            '0 4px 6px rgba(0,0,0,.04), 0 12px 32px rgba(0,0,0,.12)',
          border: '1px solid rgba(0,0,0,.06)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 14,
          textAlign: 'center',
        }}
      >
        <Spinner accessibilityLabel={label} size="large" />
        <div>
          <Text as="p" variant="bodyMd" fontWeight="semibold">
            {label}
          </Text>
          {detail ? (
            <div style={{ marginTop: 4 }}>
              <Text as="p" tone="subdued" variant="bodySm">
                {detail}
              </Text>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Tracks dirty state by comparing a JSON snapshot of form values to a clean baseline.
 * Call `markClean(value)` after load and after a successful save (pass the saved value).
 */
export function useUnsavedChanges<T>(value: T) {
  const [baseline, setBaseline] = useState<string | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  const serialized = useMemo(() => stableStringify(value), [value]);

  // No baseline yet (still loading) → not dirty
  const dirty = baseline !== null && serialized !== baseline;

  const markClean = useCallback((next?: T) => {
    const source = next !== undefined ? next : valueRef.current;
    setBaseline(stableStringify(source));
  }, []);

  const getBaseline = useCallback((): T | null => {
    if (baseline === null) return null;
    try {
      return deepClone(JSON.parse(baseline) as T);
    } catch {
      return null;
    }
  }, [baseline]);

  /**
   * After restoring fields from baseline, call this so dirty clears even if
   * React state flush is slightly async (common discard flake).
   * Always pins baseline to the restored shape — never re-baseline from a
   * possibly-stale valueRef (that was re-dirtying forms after Discard).
   */
  const markCleanAfterDiscard = useCallback((restored: T) => {
    const clean = stableStringify(restored);
    setBaseline(clean);
    // After React applies field restores, pin again to restored shape
    window.requestAnimationFrame(() => {
      setBaseline(clean);
    });
    window.setTimeout(() => {
      setBaseline(clean);
    }, 0);
  }, []);

  return { dirty, markClean, getBaseline, markCleanAfterDiscard, serialized };
}

function deepClone<T>(value: T): T {
  try {
    if (typeof structuredClone === 'function') {
      return structuredClone(value);
    }
  } catch {
    /* fall through */
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

function stableStringify(value: unknown): string {
  return JSON.stringify(value, (_k, v) => {
    if (v instanceof Date) {
      return v.toISOString();
    }
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const sorted: Record<string, unknown> = {};
      for (const key of Object.keys(v as object).sort()) {
        const val = (v as Record<string, unknown>)[key];
        // Drop undefined so baseline/current compare stably
        if (val !== undefined) sorted[key] = val;
      }
      return sorted;
    }
    return v;
  });
}
